import React from "react";

/**
 * DP-3147 — solid calculator with a dollar coin on its corner, for the Itinerary Planning cost
 * calculator trigger.
 *
 * Everything is filled with `currentColor`, so the trigger button's hover/active colour drives
 * the whole icon. The display, keys, coin gap and the "$" are all **masked out** rather than
 * painted white, so the icon reads correctly on any surface instead of assuming a white one
 * behind it. The "$" reuses the glyph from DollarIcon, scaled into the coin, rather than a
 * second hand-authored dollar path.
 */
export const CalculatorDollarIcon = ({size = 16}: {size?: number}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 512 512"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="calculator-dollar-icon"
      aria-hidden="true"
    >
      {/* Body shape, with the display, keypad and the coin's surrounding gap knocked out. */}
      <mask id="dp3147-calc-body">
        <rect x="0" y="0" width="405" height="512" rx="46" fill="white" />
        <rect x="52" y="45" width="298" height="105" rx="16" fill="black" />
        <rect x="52" y="190" width="82" height="66" rx="15" fill="black" />
        <rect x="159" y="190" width="82" height="66" rx="15" fill="black" />
        <rect x="266" y="190" width="82" height="66" rx="15" fill="black" />
        <rect x="52" y="290" width="82" height="66" rx="15" fill="black" />
        <rect x="159" y="290" width="82" height="66" rx="15" fill="black" />
        <rect x="266" y="290" width="82" height="66" rx="15" fill="black" />
        <rect x="52" y="392" width="82" height="66" rx="15" fill="black" />
        <rect x="159" y="392" width="82" height="66" rx="15" fill="black" />
        <rect x="266" y="392" width="82" height="66" rx="15" fill="black" />
        <circle cx="390" cy="385" r="140" fill="black" />
      </mask>
      <rect x="0" y="0" width="405" height="512" rx="46" fill="currentColor" mask="url(#dp3147-calc-body)" />

      {/* "1" on the display — drawn back in on top of the knocked-out panel. */}
      <path d="M292 84l9-7v46" stroke="currentColor" strokeWidth="15" strokeLinecap="round" strokeLinejoin="round" />

      {/* Coin, with the dollar glyph knocked out of it. */}
      <mask id="dp3147-calc-coin">
        <circle cx="390" cy="385" r="122" fill="white" />
        <g transform="translate(275.6 275.6) scale(10.4)">
          <path
            d="M11.0212 4C11.5104 4 11.9056 4.39102 11.9056 4.875V5.85117C11.9498 5.85664 11.9913 5.86211 12.0355 5.87031C12.0466 5.87305 12.0548 5.87305 12.0659 5.87578L13.3925 6.11641C13.8734 6.20391 14.1912 6.66055 14.1028 7.13359C14.0144 7.60664 13.5528 7.92383 13.0747 7.83633L11.7619 7.59844C10.8968 7.47266 10.134 7.55742 9.59785 7.76797C9.06168 7.97852 8.84611 8.26836 8.79636 8.53633C8.74108 8.82891 8.78254 8.99297 8.82952 9.09414C8.87927 9.20078 8.98153 9.32109 9.18329 9.45508C9.63378 9.74766 10.3247 9.93906 11.2202 10.1742L11.3003 10.1961C12.0908 10.4039 13.0581 10.6555 13.7767 11.1203C14.1691 11.3746 14.5395 11.7191 14.7689 12.2004C15.0038 12.6898 15.0535 13.2367 14.9457 13.8191C14.755 14.8582 14.0309 15.5527 13.1327 15.9164C12.7541 16.0695 12.3423 16.168 11.9056 16.2172V17.125C11.9056 17.609 11.5104 18 11.0212 18C10.532 18 10.1368 17.609 10.1368 17.125V16.1707C10.1257 16.168 10.1119 16.168 10.1009 16.1652H10.0953C9.42097 16.0613 8.3127 15.7742 7.56648 15.4461C7.12151 15.2492 6.91976 14.7324 7.11875 14.2895C7.31774 13.8465 7.84009 13.6496 8.28506 13.8465C8.86269 14.1008 9.81343 14.3523 10.3634 14.4371C11.2451 14.5656 11.9719 14.4918 12.4639 14.2922C12.931 14.1035 13.1438 13.8301 13.2046 13.502C13.2571 13.2121 13.2156 13.0453 13.1686 12.9441C13.1161 12.8348 13.0139 12.7145 12.8094 12.5805C12.3561 12.2879 11.6624 12.0965 10.7642 11.8613L10.6868 11.8422C9.8991 11.6371 8.93178 11.3828 8.2132 10.918C7.82075 10.6637 7.45317 10.3164 7.22377 9.83516C6.99438 9.34297 6.9474 8.79609 7.05518 8.21367C7.25418 7.17188 8.04461 6.49375 8.94284 6.14102C9.31042 5.99609 9.71393 5.89766 10.1368 5.84023V4.875C10.1368 4.39102 10.532 4 11.0212 4Z"
            fill="black"
          />
        </g>
      </mask>
      <circle cx="390" cy="385" r="122" fill="currentColor" mask="url(#dp3147-calc-coin)" />
    </svg>
  );
};
